<template>
  <div
    class="BaseTemplateMainSection relative-position"
    :class="{
      'BaseTemplateMainSection--active': !templateStore.isDigitalMenu && isLayerActiveRef,
      'BaseTemplateMainSection--moving': isLayerMovingRef,
      'BaseTemplateMainSection--printer': hasPrinter,
      'BaseTemplateMainSection--digital-menu': templateStore.isDigitalMenu,
    }"
    :style="backgroundStyle"
  >
    <div
      :id="layer.id"
      :style="`height: ${layer.height}px`"
      class="overflow-hidden relative-position"
      @click="
        templateStore.handleActiveLayer({
          index: layerIndex,
          id: layer.id,
          updatedFrom: 'section',
        })
      "
      @click.self="templateStore.handleActiveItem({})"
    >
      <div
        v-for="(item, itemIndex) in layer.items"
        :key="itemIndex"
        class="absolute flex"
        :class="{ hidden: !item.is_show, transition: hasItemTransitionRef }"
        :style="`top: ${item.start_position.y}px; left: ${item.start_position.x}px; z-index: ${
          980 - itemIndex
        }; transform: rotate(${item.rotate}deg)`"
      >
        <k-icon
          v-if="item.is_field && !hasPrinter"
          name="link-2"
          class="transition cursor-pointer BaseTemplateMainSection__field-icon"
          color="info-70"
          size="16px"
          @click="templateStore.handleActiveItem(item)"
        />
        <BaseTemplateContentText
          v-if="item.type === 'text'"
          class="BaseTemplateMainSection__item-content"
          :class="{
            'BaseTemplateMainSection__item-content--active': item.id === templateStore.activeItem.id,
            'BaseTemplateMainSection__item-content--printer': hasPrinter,
          }"
          :item="item"
          :item-index="itemIndex"
          :layer-index="layerIndex"
          :has-printer="hasPrinter"
        />
        <span
          v-if="item.type === 'image'"
          class="BaseTemplateMainSection__item-content flex BaseTemplateMainSection__item-shape-image-content transition"
          :class="{
            'BaseTemplateMainSection__item-content--active BaseTemplateMainSection__item-shape-image-content--active':
              item.id === templateStore.activeItem.id,
            'BaseTemplateMainSection__item-shape-image-content--printer': hasPrinter,
          }"
          @click="templateStore.handleActiveItem(item)"
        >
          <img
            class="BaseTemplateMainSection__image"
            :src="
              templateStore.newUploadImages[item.id]?.blob ??
              `${templateStore.layers[layerIndex].items[itemIndex].value}?t=${currentDate}`
            "
            :style="[`width: ${item.width}px; height: ${item.height}px; border-radius: ${item.radius}px`]"
            :class="{ 'BaseTemplateMainSection__image-mono-color': templateStore.isPrintTemplate }"
            @error="$event.target.src = addImage"
          />
        </span>
        <BaseTemplateContentShape
          v-if="item.type === 'shape'"
          class="BaseTemplateMainSection__item-content BaseTemplateMainSection__item-shape-image-content"
          :class="{
            'BaseTemplateMainSection__item-content--active BaseTemplateMainSection__item-shape-image-content--active':
              item.id === templateStore.activeItem.id,
            'BaseTemplateMainSection__item-shape-image-content--printer': hasPrinter,
          }"
          :item="item"
        />

        <k-circle-icon
          v-if="!hasPrinter"
          name="move"
          size="18px"
          background="white"
          draggable="true"
          root-class="absolute-top-right q-pa-xs BaseTemplateMainSection__content-move"
          @dragstart.self="handleItemMoveStart(item)"
          @drag.self="handleItemMove($event, itemIndex)"
          @dragend.self="handleItemMove($event, itemIndex)"
        />
      </div>
    </div>
    <span v-if="!hasPrinter && !templateStore.isDigitalMenu" class="BaseTemplateMainSection__icon transition flex">
      <k-icon
        name="draggable"
        size="30px"
        draggable="true"
        @drag.self="handleResize($event)"
        @dragend.self="handleResize($event)"
      />
      <k-tooltip label="Extend" position="top" />
    </span>
  </div>
</template>

<script setup>
import useBaseTemplateMainSectionHook from './BaseTemplateMainSectionHook'
import BaseTemplateContentShape from './BaseTemplateContentShape'
import BaseTemplateContentText from './BaseTemplateContentText'
import addImage from 'src/assets/add-image.svg'

const props = defineProps({
  layerIndex: { type: Number, required: true },
  layer: { type: Object, required: true },
  hasPrinter: { type: Boolean, default: false },
})

const {
  templateStore,
  currentDate,
  hasItemTransitionRef,
  isLayerMovingRef,
  isLayerActiveRef,
  backgroundStyle,
  handleResize,
  handleItemMove,
  handleItemMoveStart,
} = useBaseTemplateMainSectionHook(props)
</script>

<style lang="sass" scoped>
@import 'src/shared/styles/variables.sass'
@import 'src/shared/styles/mixins.sass'

%section-extend-icon-active
  opacity: 1
  transform: translate(50%, 50%) rotate(0)
  pointer-events: all
  cursor: grab
  z-index: 981
  background-color: rgba(167, 211, 235, 0.20)
  border-radius: 100%
%content-move-icon-active
  opacity: 1
  transform: translate(50%, -50%) rotate(0)
  pointer-events: all
  cursor: grab
  z-index: 2
%field-icon-inavtive
  transform: scale(0)
  opacity: 0
  visibility: hidden
  pointer-events: none


.BaseTemplateMainSection
  transition: 0.3s ease background
  background-image: none
  &:hover::before
    content: ''
    position: absolute
    z-index: 981
    width: 100%
    height: 100%
    top: 0
    left: 0
    pointer-events: none
    // generated with: https://kovart.github.io/dashed-border-generator/
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%231B72A4' stroke-width='4' stroke-dasharray='4%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")
  &:hover
    background-color: rgba(167, 211, 235, 0.20)
    .BaseTemplateMainSection__icon
      @extend %section-extend-icon-active
  @include state-modifier('active')
    background-color: rgba(167, 211, 235, 0.20)
    &::before
      content: ''
      position: absolute
      z-index: 981
      width: 100%
      height: 100%
      top: 0
      left: 0
      pointer-events: none
      // generated with: https://kovart.github.io/dashed-border-generator/
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%231B72A4' stroke-width='4' stroke-dasharray='4%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")
    .BaseTemplateMainSection__icon
      @extend %section-extend-icon-active

  @include state-modifier('printer')
    background-image: none
    &::before
      content: none !important

  @include state-modifier('digital-menu')
    &:hover
      background-color: transparent
      &::before
        content: none

  @include element('field-icon')
    position: absolute
    top: 0
    left: 0
    transform: translate(-100%, -50%)
    top: 50%


  @include element('icon')
    opacity: 0
    z-index: 981
    pointer-events: none
    position: absolute
    bottom: 0
    right: 0
    transform: translate(50%, 50%) rotate(180deg)
    &:hover
      @extend %section-extend-icon-active
  @include element('content-move')
    opacity: 0
    z-index: 1
    position: absolute
    pointer-events: none
    transform: translate(50%, -50%) rotate(180deg)
    &:hover
      @extend %content-move-icon-active

  @include element('item-content')
    border: none
    background-color: var(--transparent)
    outline: none
    &:hover, &:focus, &:active, &:focus-within
      border: none
      outline: none
      background-color: rgba(255, 255, 255, 0.5)
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%2300A8DD' stroke-width='4' stroke-dasharray='4%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")
    &:hover ~ .BaseTemplateMainSection__content-move
        @extend %content-move-icon-active
    &:hover ~ .BaseTemplateMainSection__field-icon
        @extend %field-icon-inavtive

    @include state-modifier('active')
      border: none
      outline: none
      background-color: rgba(255, 255, 255, 0.5)
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%2300A8DD' stroke-width='4' stroke-dasharray='4%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")
      & ~ .BaseTemplateMainSection__content-move
        @extend %content-move-icon-active
      & ~ .BaseTemplateMainSection__field-icon
        @extend %field-icon-inavtive

    @include state-modifier('printer')
      background-image: none
      border: none
      &:hover, &:focus, &:active, &:focus-within
        background-image: none

  @include element('item-shape-image-content')
    padding: 2px
    box-sizing: content-box
    object-fit: scale-down
    object-position: center
    background-image: none
    border: 2px dashed transparent

    &:hover, &:focus, &:active, &:focus-within
      background-image: none
      border: 2px dashed #00A8DD

    @include state-modifier('active')
      background-image: none
      border: 2px dashed #00A8DD

    @include state-modifier('printer')
      background-image: none
      border: none
      &:hover, &:focus, &:active, &:focus-within
        background-image: none
        border: none
  @include element('image')
    object-fit: cover
  @include element('image-mono-color')
    filter: grayscale(100%) contrast(10000%) brightness(150%)
</style>

<style lang="sass">
/* FIXME @Kasra we cant use :global for this style, it doesnt work */
.BaseTemplateMain:has(.BaseTemplateMainSection--moving)
  .BaseTemplateMainSection:not(.BaseTemplateMainSection--moving)
    pointer-events: none
</style>
